import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CURRENCIES, INVESTMENT_TYPES, api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Home } from "lucide-react";
import { format, parseISO } from "date-fns";

const PROPERTY_TYPES = [
  { value: "piso", label: "Piso" },
  { value: "casa", label: "Casa" },
  { value: "local", label: "Local comercial" },
  { value: "garaje", label: "Garaje / trastero" },
  { value: "terreno", label: "Terreno" },
  { value: "otro", label: "Otro" },
];

function PurchaseDateField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="inv-purchase-date"
          type="button"
          className="w-full flex items-center gap-2 px-3 h-10 rounded-md border border-input bg-background text-sm text-left"
        >
          <CalendarIcon className="w-4 h-4 text-[#8A9992]" />
          <span className={value ? "text-[#0A110E]" : "text-[#8A9992]"}>
            {value ? format(parseISO(value), "PPP") : "Fecha"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar
          mode="single"
          selected={value ? parseISO(value) : null}
          disabled={{ after: new Date() }}
          onSelect={(d) => { onChange(d ? d.toISOString() : null); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
}

const EMPTY = {
  name: "",
  type: "stock",
  ticker: "",
  isin: "",
  quantity: 0,
  purchase_price: 0,
  current_price: null,
  currency: "USD",
  notes: "",
  address: "",
  property_type: "piso",
  purchase_date: null,
  rental_income: null,
  linked_mortgage_id: null,
  annual_property_tax: null,
  rental_tax_rate: null,
  revaluation_threshold: null,
  target_price_up: null,
  target_price_down: null,
  interest_rate: null,
  maturity_date: null,
};

export default function InvestmentFormDialog({ open, onOpenChange, initial, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mortgages, setMortgages] = useState([]);

  useEffect(() => {
    if (open) setForm(initial || EMPTY);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    if (form.type !== "real_estate") return;
    api.get("/debts")
      .then((r) => setMortgages((r.data || []).filter((d) => d.is_mortgage)))
      .catch((e) => logger.warn("mortgages fetch", e));
  }, [open, form.type]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleTickerSearch = async (q) => {
    set("ticker", q);
    if (form.type !== "crypto" || !q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/prices/crypto/search?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data.coins || []);
    } catch { setSuggestions([]); }
  };

  const pickCoin = async (coin) => {
    set("ticker", coin.id);
    set("name", form.name || coin.name);
    setSuggestions([]);
    try {
      const r = await api.get(`/prices/crypto?ids=${coin.id}&vs=usd`);
      if (r.data[coin.id]?.usd) set("current_price", r.data[coin.id].usd);
    } catch (e) {
      logger.warn("coin price fetch", e);
    }
  };

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Ingresa un nombre");
    setSaving(true);
    try {
      const payload = {
        ...form,
        isin: form.isin ? String(form.isin).toUpperCase().trim() : null,
        quantity: Number(form.quantity) || 0,
        purchase_price: Number(form.purchase_price) || 0,
        current_price: form.current_price === null || form.current_price === "" ? null : Number(form.current_price),
        rental_income: form.rental_income === null || form.rental_income === "" ? null : Number(form.rental_income),
        annual_property_tax: form.annual_property_tax === null || form.annual_property_tax === "" ? null : Number(form.annual_property_tax),
        rental_tax_rate: form.rental_tax_rate === null || form.rental_tax_rate === "" ? null : Number(form.rental_tax_rate),
        revaluation_threshold: form.revaluation_threshold === null || form.revaluation_threshold === "" ? null : Number(form.revaluation_threshold),
        target_price_up: form.target_price_up === null || form.target_price_up === "" ? null : Number(form.target_price_up),
        target_price_down: form.target_price_down === null || form.target_price_down === "" ? null : Number(form.target_price_down),
        interest_rate: form.interest_rate === null || form.interest_rate === "" ? null : Number(form.interest_rate),
        maturity_date: form.maturity_date || null,
      };
      // Clean fields that don't apply to non-real-estate types (except purchase_date,
      // which we also reuse for deposits to compute accrued interest).
      if (payload.type !== "real_estate") {
        payload.address = null;
        payload.property_type = null;
        payload.rental_income = null;
        payload.linked_mortgage_id = null;
        payload.annual_property_tax = null;
        payload.rental_tax_rate = null;
        payload.revaluation_threshold = null;
      }
      // Purchase date only applies to real estate and deposits — clear otherwise.
      if (payload.type !== "real_estate" && payload.type !== "deposit") {
        payload.purchase_date = null;
      }
      // Target price only makes sense for tradable assets
      if (!["stock", "fund", "crypto"].includes(payload.type)) {
        payload.target_price_up = null;
        payload.target_price_down = null;
      }
      // Interest rate + maturity only apply to deposits
      if (payload.type !== "deposit") {
        payload.interest_rate = null;
        payload.maturity_date = null;
      }
      // ISIN only applies to stock/fund. Clear it and cached symbol otherwise.
      if (payload.type !== "stock" && payload.type !== "fund") {
        payload.isin = null;
        payload.yahoo_symbol = null;
      } else if (payload.isin === null || payload.isin === undefined) {
        // ISIN was removed → also clear the cached symbol so it re-resolves next time
        payload.yahoo_symbol = null;
      } else if (initial?.isin && initial.isin !== payload.isin) {
        // ISIN was changed → invalidate cached symbol
        payload.yahoo_symbol = null;
      }
      if (initial?.id) {
        await api.put(`/investments/${initial.id}`, payload);
        toast.success("Inversión actualizada");
      } else {
        await api.post("/investments", payload);
        toast.success("Inversión agregada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial?.id ? "Editar inversión" : "Nueva inversión"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label>Nombre</Label>
            <Input data-testid="inv-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej: Bitcoin, Apple, Depto CDMX" />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="inv-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INVESTMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="inv-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(form.type === "crypto" || form.type === "stock" || form.type === "fund") && (
            <div className="col-span-2 relative">
              <Label>
                {form.type === "crypto" ? "ID CoinGecko (ej: bitcoin)"
                  : form.type === "fund" ? "Ticker (opcional)"
                  : "Ticker (ej: AAPL)"}
              </Label>
              <Input
                data-testid="inv-ticker"
                value={form.ticker || ""}
                onChange={(e) => handleTickerSearch(e.target.value)}
                placeholder={form.type === "crypto" ? "bitcoin" : "AAPL"}
              />
              {suggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-[#EBEBEA] rounded-xl shadow-lg max-h-56 overflow-auto">
                  {suggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pickCoin(c)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#FAFAF9]"
                    >
                      {c.thumb && <img src={c.thumb} alt="" className="w-5 h-5" />}
                      <span className="text-sm font-medium">{c.name}</span>
                      <span className="text-xs text-[#8A9992] ml-auto">{c.symbol}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {(form.type === "stock" || form.type === "fund") && (
            <div className="col-span-2">
              <Label>ISIN (opcional, 12 caracteres)</Label>
              <Input
                data-testid="inv-isin"
                value={form.isin || ""}
                onChange={(e) => set("isin", e.target.value.toUpperCase().slice(0, 12))}
                placeholder="Ej: US0378331005, IE00BK5BQT80"
                maxLength={12}
                className="uppercase tabular-nums"
              />
              <p className="text-xs text-[#8A9992] mt-1">
                Si añades el ISIN, actualizaremos el precio a diario con Yahoo Finance
                (ideal para ETFs y fondos europeos). Sobrescribe el precio manual.
              </p>
            </div>
          )}

          {(form.type === "stock" || form.type === "fund" || form.type === "crypto") && (
            <div className="col-span-2 rounded-xl border border-[#C89A3F]/25 bg-[#C89A3F]/5 p-4">
              <div className="flex items-center gap-2 text-[#8B6B1F] mb-2">
                <span className="text-xs uppercase tracking-[0.08em] font-bold">🎯 Objetivos de precio</span>
              </div>
              <p className="text-xs text-[#5F7368] mb-3">
                Recibirás una notificación push cuando el precio cruce estos niveles.
                Deja vacío para no recibir alertas.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Objetivo al alza (↑)</Label>
                  <Input
                    type="number"
                    step="any"
                    data-testid="inv-target-up"
                    value={form.target_price_up ?? ""}
                    onChange={(e) => set("target_price_up", e.target.value === "" ? null : e.target.value)}
                    placeholder="Ej: 350"
                  />
                </div>
                <div>
                  <Label>Objetivo a la baja (↓)</Label>
                  <Input
                    type="number"
                    step="any"
                    data-testid="inv-target-down"
                    value={form.target_price_down ?? ""}
                    onChange={(e) => set("target_price_down", e.target.value === "" ? null : e.target.value)}
                    placeholder="Ej: 250"
                  />
                </div>
              </div>
            </div>
          )}

          {form.type === "deposit" && (
            <div className="col-span-2 grid grid-cols-3 gap-3">
              <div>
                <Label>{t("investmentDialog.interestRate", { defaultValue: "Annual interest rate (%)" })}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  data-testid="inv-interest-rate"
                  value={form.interest_rate ?? ""}
                  onChange={(e) => set("interest_rate", e.target.value === "" ? null : e.target.value)}
                  placeholder={t("investmentDialog.interestRatePlaceholder", { defaultValue: "e.g. 3.25" })}
                />
              </div>
              <div>
                <Label>{t("investmentDialog.depositStart", { defaultValue: "Start date" })}</Label>
                <Input
                  type="date"
                  data-testid="inv-purchase-date"
                  value={form.purchase_date ?? ""}
                  onChange={(e) => set("purchase_date", e.target.value === "" ? null : e.target.value)}
                />
              </div>
              <div>
                <Label>{t("investmentDialog.maturityDate", { defaultValue: "Maturity date" })}</Label>
                <Input
                  type="date"
                  data-testid="inv-maturity-date"
                  value={form.maturity_date ?? ""}
                  onChange={(e) => set("maturity_date", e.target.value === "" ? null : e.target.value)}
                />
              </div>
              <p className="col-span-3 text-xs text-[#8A9992] mt-0">
                {t("investmentDialog.depositHint", { defaultValue: "We show the accrued interest to date (simple interest × days ÷ 365). Not included in net worth." })}{" "}
                {t("investmentDialog.maturityHint", { defaultValue: "You'll get an alert 30 days before maturity." })}
              </p>
            </div>
          )}

          {form.type === "real_estate" && (
            <div className="col-span-2 rounded-xl border border-[#1C7C54]/20 bg-[#1C7C54]/5 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-[#1C7C54]">
                <Home className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.08em] font-bold">Detalles del inmueble</span>
              </div>
              <div>
                <Label>Dirección</Label>
                <Input
                  data-testid="inv-address"
                  value={form.address || ""}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Calle, ciudad"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.property_type || "piso"} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger data-testid="inv-property-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha de compra</Label>
                  <PurchaseDateField
                    value={form.purchase_date}
                    onChange={(iso) => set("purchase_date", iso)}
                  />
                </div>
              </div>
              <div>
                <Label>Renta mensual (opcional)</Label>
                <Input
                  type="number"
                  step="any"
                  data-testid="inv-rental"
                  value={form.rental_income ?? ""}
                  onChange={(e) => set("rental_income", e.target.value === "" ? null : e.target.value)}
                  placeholder="Deja vacío si no está alquilado"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Impuesto anual (IBI)</Label>
                  <Input
                    type="number"
                    step="any"
                    data-testid="inv-property-tax"
                    value={form.annual_property_tax ?? ""}
                    onChange={(e) => set("annual_property_tax", e.target.value === "" ? null : e.target.value)}
                    placeholder="Ej: 480"
                  />
                </div>
                <div>
                  <Label>Retención IRPF (%)</Label>
                  <Input
                    type="number"
                    step="any"
                    data-testid="inv-rental-tax-rate"
                    value={form.rental_tax_rate ?? ""}
                    onChange={(e) => set("rental_tax_rate", e.target.value === "" ? null : e.target.value)}
                    placeholder="Ej: 19"
                  />
                </div>
              </div>
              <div>
                <Label>Umbral alerta revalorización (%)</Label>
                <Input
                  type="number"
                  step="any"
                  data-testid="inv-revaluation-threshold"
                  value={form.revaluation_threshold ?? ""}
                  onChange={(e) => set("revaluation_threshold", e.target.value === "" ? null : e.target.value)}
                  placeholder="Por defecto 10"
                />
                <p className="text-xs text-[#8A9992] mt-1">
                  Cambio en % entre tasaciones que dispara la alerta. Vacío = 10% global.
                </p>
              </div>
              <div>
                <Label>Hipoteca vinculada (opcional)</Label>
                <Select
                  value={
                    form.linked_mortgage_id && mortgages.some((m) => m.id === form.linked_mortgage_id)
                      ? form.linked_mortgage_id
                      : "none"
                  }
                  onValueChange={(v) => set("linked_mortgage_id", v === "none" ? null : v)}
                >
                  <SelectTrigger data-testid="inv-linked-mortgage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin hipoteca</SelectItem>
                    {mortgages.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.creditor} — {m.description || "hipoteca"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {mortgages.length === 0 && (
                  <p className="text-xs text-[#8A9992] mt-1">
                    Marca una deuda como "Es una hipoteca" en Deudas para poder vincularla aquí.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <Label>{form.type === "real_estate" ? "Unidades" : "Cantidad"}</Label>
            <Input type="number" step="any" data-testid="inv-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
          <div>
            <Label>{form.type === "real_estate" ? "Precio de compra total" : "Precio de compra (u.)"}</Label>
            <Input type="number" step="any" data-testid="inv-purchase" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} />
          </div>

          <div className="col-span-2">
            <Label>{form.type === "real_estate" ? "Valor actual (opcional)" : "Precio actual (u.) — opcional"}</Label>
            <Input type="number" step="any" data-testid="inv-current"
              value={form.current_price ?? ""}
              onChange={(e) => set("current_price", e.target.value)}
              placeholder={form.type === "real_estate" ? "Última tasación / valor de mercado" : "Deja vacío para usar el precio de compra"}
            />
          </div>

          <div className="col-span-2">
            <Label>Notas</Label>
            <Textarea data-testid="inv-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            data-testid="inv-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-[#0F291E] hover:bg-[#1C7C54]"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
